import { Button, Group } from "@mantine/core";
import type { FormManager, FormSelector, FormSubscription } from "@ngt-form/core";
import { FormActionButton } from "@ngt-form/mantine";
import type { FC, MouseEvent, PropsWithChildren } from "react";

import type { TMDValidationError } from "src/types/TMDValidationError";

export enum TMDFormSaveAction {
    Submit = 1,
    SubmitAndReturn = 2,
    SaveDraft = 3
}

export type TMDFormSubmitMetadata = {
    saveAction: TMDFormSaveAction;
};

const loadingSelector: FormSelector<
    any,
    TMDValidationError,
    TMDFormSubmitMetadata,
    boolean
> = snapshot => snapshot.submitting;
const loadingSubscription: Partial<FormSubscription> = { submitting: true };

/**
 * Sets Submit metadata before requesting form submission.
 *
 * Postconditions: Submission is started without awaiting or returning its promise; return does
 * not establish a successful save. The form submission lifecycle owns subsequent outcomes.
 */
const onSubmitClick = (
    _event: MouseEvent<HTMLButtonElement>,
    formManager: FormManager<any, TMDValidationError, TMDFormSubmitMetadata>
) => {
    formManager.setMetadataValue("saveAction", TMDFormSaveAction.Submit);
    formManager.submit();
};

/**
 * Sets SubmitAndReturn metadata before requesting form submission.
 *
 * Postconditions: Submission is started without awaiting or returning its promise. Navigation
 * belongs to the owning submit callback and is not performed by this click handler.
 */
const onSubmitAndReturnClick = (
    _event: MouseEvent<HTMLButtonElement>,
    formManager: FormManager<any, TMDValidationError, TMDFormSubmitMetadata>
) => {
    formManager.setMetadataValue("saveAction", TMDFormSaveAction.SubmitAndReturn);
    formManager.submit();
};

/** Requests a draft save through the owning form submission lifecycle. */
const onSaveDraftClick = (
    _event: MouseEvent<HTMLButtonElement>,
    formManager: FormManager<any, TMDValidationError, TMDFormSubmitMetadata>
) => {
    formManager.setMetadataValue("saveAction", TMDFormSaveAction.SaveDraft);
    formManager.submit();
};

type TMDFormSubmitButtonsProps = PropsWithChildren<{}> & {
    canEdit: boolean;
    showSubmitAndReturn: boolean;
    showSubmit: boolean;
    showCancel: boolean;
    showSaveDraft?: boolean;
    onCancel?: () => void;

    submitAndReturnLabel?: string | null | undefined;
    submitLabel?: string | null | undefined;
    cancelLabel?: string | null | undefined;
};

/**
 * Displays configured save/cancel actions and caller-provided controls.
 *
 * Preconditions: FormActionButton receives the owning form context.
 * Postconditions: Save actions require canEdit; cancel visibility is independent. Explicit
 * loading subscriptions follow submitting. Cancel text uses the truthiness of cancelLabel ??
 * canEdit to choose Cancel or Back; a supplied label is not rendered verbatim.
 */
const TMDFormSubmitButtons: FC<TMDFormSubmitButtonsProps> = ({
    canEdit,
    showSubmit,
    showSubmitAndReturn,
    showCancel,
    showSaveDraft = false,
    cancelLabel,
    onCancel,
    submitAndReturnLabel,
    submitLabel,
    children
}) => {
    return (
        <Group
            gap="md"
            justify="flex-end"
        >
            {children}
            {showSaveDraft && canEdit && (
                <FormActionButton
                    loading={loadingSelector}
                    loadingSubscription={loadingSubscription}
                    onClick={onSaveDraftClick}
                    variant="outline"
                >
                    Save Draft
                </FormActionButton>
            )}
            {showSubmit && canEdit && (
                <FormActionButton
                    loading={loadingSelector}
                    loadingSubscription={loadingSubscription}
                    onClick={onSubmitClick}
                >
                    {submitLabel ?? "Submit"}
                </FormActionButton>
            )}
            {showSubmitAndReturn && canEdit && (
                <FormActionButton
                    loading={loadingSelector}
                    loadingSubscription={loadingSubscription}
                    onClick={onSubmitAndReturnClick}
                >
                    {submitAndReturnLabel ?? "Submit And Return"}
                </FormActionButton>
            )}
            {showCancel && (
                <Button
                    color="gray.6"
                    onClick={onCancel}
                >
                    {(cancelLabel ?? canEdit) ? "Cancel" : "Back"}
                </Button>
            )}
        </Group>
    );
};

export default TMDFormSubmitButtons;
